import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/auth";
import Get from "../../helpers/ajax/get";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
const moment = require("moment");

const SubmitterJobs = () => {
    const { logout } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [filter, setFilter] = useState({});
    const [error, setError] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [colonies, setColonies] = useState([]);
    const [workers, setWorkers] = useState([]);
    const [workTypes, setWorkTypes] = useState([]);

    const fetchJobs = async (page, filterObj) => {
        const response = await Get(`/api/submitterJobs?page=${page}&limit=10&filter=${JSON.stringify(filterObj)}`);
        return response;
    };

    const loadPage = async (page, filterObj) => {
        setError("");
        try {
            const response = await fetchJobs(page, filterObj || filter);
            if (page === 1) {
                setJobs(response.jobs || []);
            } else {
                setJobs((prev) => [...prev, ...(response.jobs || [])]);
            }
            setTotalCount(response.totalCount || 0);
            setHasMore((response.jobs || []).length === 10);
        } catch (err) {
            setError(err.message || err.error || "Error loading jobs");
        }
    };

    useEffect(() => {
        loadPage(1, {});
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        Get("/api/submitters/getFormInfo")
            .then((data) => {
                setColonies(data.colonies || []);
                setWorkers(data.workers || []);
                setWorkTypes(data.workTypes || []);
            })
            .catch(console.log);
    }, []);

    const applyFilter = (newFilter) => {
        setFilter(newFilter);
        setCurrentPage(1);
        loadPage(1, newFilter);
    };

    const resetFilter = () => {
        setFilter({});
        setCurrentPage(1);
        loadPage(1, {});
    };

    const loadMore = () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        loadPage(nextPage, filter);
    };

    return (
        <div className="bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto p-4">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-bold text-dependable">My Work Tickets</h1>
                    <button onClick={logout} className="bg-dependable text-white px-4 py-2 rounded-md font-semibold">
                        Logout
                    </button>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 p-4 mb-2 rounded relative" role="alert">
                        <strong className="font-bold">Error: </strong>{" " + error}
                        <button className="absolute top-2 right-2" onClick={() => setError("")}>✕</button>
                    </div>
                )}

                <FilterAccordion
                    filter={filter}
                    colonies={colonies}
                    workers={workers}
                    workTypes={workTypes}
                    onApply={applyFilter}
                    onReset={resetFilter}
                />

                <div className="text-center p-4 font-semibold text-dependable">Total Tickets Found: {totalCount}</div>

                <div className="grid grid-cols-3 gap-4 items-center p-4 font-bold text-dependable">
                    <p>Date</p>
                    <p>Worker's Name</p>
                    <p>Location</p>
                </div>

                {jobs.map((job) => (
                    <JobAccordion key={job._id} job={job} />
                ))}

                {jobs.length === 0 && !error && (
                    <div className="text-center p-8 text-gray-500">No work tickets found.</div>
                )}

                <div className="flex justify-center mt-4">
                    <button
                        onClick={loadMore}
                        disabled={!hasMore}
                        className={`px-4 py-2 rounded-md ${hasMore ? "bg-dependable text-white hover:bg-dependable-green" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                    >
                        Show More
                    </button>
                </div>
            </div>
        </div>
    );
};

const JobAccordion = ({ job }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className={`bg-white shadow-md rounded-lg mb-4 overflow-hidden ${expanded ? "border-2 border-dependable-green" : ""}`}>
            <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
                <div className="flex-grow grid grid-cols-3 gap-4">
                    <p className="text-sm md:text-base text-dependable font-semibold">{moment(job.date).format("MM/DD/YYYY")}</p>
                    <p className="text-sm md:text-base text-dependable font-semibold">{job.workersName}</p>
                    <p className="text-sm md:text-base text-dependable font-semibold">{job.place}</p>
                </div>
                <div className={`text-dependable-green transition-transform duration-300 ${expanded ? "transform rotate-180" : ""}`}>
                    {expanded ? <FaChevronUp /> : <FaChevronDown />}
                </div>
            </div>
            <div className={`overflow-hidden transition-max-height duration-300 ease-in-out ${expanded ? "" : "max-h-0"}`}>
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="text-center font-semibold text-dependable mb-4 overflow-hidden text-ellipsis whitespace-nowrap">
                        Ticket #: {job.ticket}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="From" value={job.timeFrom ? moment(job.timeFrom, "HH:mm").format("h:mm A") : "Not set"} />
                        <Field label="Who worked" value={job.workersName || "Not set"} />
                        <Field label="To" value={job.timeTo ? moment(job.timeTo, "HH:mm").format("h:mm A") : "Not set"} />
                        <Field label="Number of People" value={job.amountOfPeople ?? "Not set"} />
                        <Field label="Duration" value={job.duration || "Not set"} />
                        <Field label="Number of Helpers" value={job.amountOfHelpers ?? "Not set"} />
                        <Field label="Type of Work" value={job.jobType || "Not set"} />
                        <Field label="Location" value={job.place || "Not set"} />
                        <Field label="Job Done" value={job.jobDone === true ? "Yes" : job.jobDone === false ? "No" : "Not set"} />
                        <Field label="Supplies Used" value={job.suppliesUsed === true ? "Yes" : job.suppliesUsed === false ? "No" : "Not set"} />
                        <Field label="Date" value={moment(job.date).format("MM/DD/YYYY")} />
                        <Field label="Task Number" value={job.taskNumber || "Not set"} />
                    </div>
                    <div className="mt-4">
                        <Field label="Note" value={job.note || "Not set"} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, value }) => (
    <p className="mb-2">
        <span className="font-semibold text-dependable">{label}: </span>
        <span className="text-gray-700">{value}</span>
    </p>
);

const FilterAccordion = ({ filter, colonies, workers, workTypes, onApply, onReset }) => {
    const [expanded, setExpanded] = useState(false);
    const [localFilter, setLocalFilter] = useState({});

    const updateFilter = (key, value) => {
        setLocalFilter((prev) => ({ ...prev, [key]: value }));
    };

    const apply = () => {
        const cleaned = Object.fromEntries(Object.entries(localFilter).filter(([, v]) => v && v !== "Any" && v !== "Not set"));
        onApply(cleaned);
    };

    const reset = () => {
        setLocalFilter({});
        onReset();
    };

    return (
        <div className="bg-white shadow-md rounded-lg mb-4 overflow-hidden text-center border-2 border-gray-400">
            <div className="flex justify-between items-center p-4 cursor-pointer bg-gray-100 hover:bg-gray-200 transition-colors duration-300" onClick={() => setExpanded(!expanded)}>
                <span className="flex-grow text-center font-semibold text-lg text-gray-800">Filter Settings</span>
                <div className={`text-dependable-green transition-transform duration-300 ${expanded ? "transform rotate-180" : ""}`}>
                    {expanded ? <FaChevronUp /> : <FaChevronDown />}
                </div>
            </div>
            <div className={`overflow-hidden transition-max-height duration-300 ease-in-out ${expanded ? "" : "max-h-0"}`}>
                <div className="p-4 bg-gray-50 border-t text-center border-gray-200 grid grid-cols-2 gap-4">
                    <FilterField label="Worker's name" value={localFilter.workersName || "Any"} onChange={(v) => updateFilter("workersName", v)} options={["Any", ...workers, "Other"]} />
                    <FilterField label="Task Number" value={localFilter.taskNumber || ""} onChange={(v) => updateFilter("taskNumber", v)} type="text" />
                    <FilterField label="Ticket Number" value={localFilter.ticket || ""} onChange={(v) => updateFilter("ticket", v)} type="text" />
                    <FilterField label="From" value={localFilter.from || ""} onChange={(v) => updateFilter("from", v)} type="date" />
                    <FilterField label="To" value={localFilter.to || ""} onChange={(v) => updateFilter("to", v)} type="date" />
                    <FilterField label="Location" value={localFilter.place || "Any"} onChange={(v) => updateFilter("place", v)} options={["Any", ...colonies, "Other"]} />
                    <FilterField label="Type of Work" value={localFilter.jobType || "Any"} onChange={(v) => updateFilter("jobType", v)} options={["Any", ...workTypes, "Other"]} />
                    <FilterField label="Supplies Used" value={localFilter.suppliesUsed || "Any"} onChange={(v) => updateFilter("suppliesUsed", v)} options={["Any", "Yes", "No"]} />
                    <div className="col-span-2 flex justify-end space-x-4 mt-4">
                        <button className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-300/80 transition-colors" onClick={reset}>Reset</button>
                        <button className="px-4 py-2 bg-dependable text-white rounded hover:bg-dependable/80 transition-colors" onClick={apply}>OK</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FilterField = ({ label, value, onChange, options, type = "select" }) => (
    <div className="text-left">
        <label className="block text-sm font-semibold text-dependable mb-1">{label}</label>
        {type === "select" ? (
            <select className="w-full block rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-dependable focus:border-transparent shadow-sm px-4 py-2 text-gray-700" value={value} onChange={(e) => onChange(e.target.value)}>
                {options.map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                ))}
            </select>
        ) : (
            <input type={type} className="w-full block rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-dependable focus:border-transparent shadow-sm px-4 py-2 text-gray-700" value={value} onChange={(e) => onChange(e.target.value)} />
        )}
    </div>
);

export default SubmitterJobs;